/**
 * CRM Service API Client Hook
 * Provides authenticated API calls to the CRM service with JWT Bearer tokens.
 * Uses the centralized useTokenManager for token lifecycle.
 */
import { useCallback } from "react";
import { useMsal } from "@azure/msal-react";

import { useDevBypass } from "../utils/devBypass";
import { useTokenManager } from "./useTokenManager";

import { getConfig } from "../config";



interface CrmApiClient {
  get: <T>(path: string) => Promise<T>;
  post: <T>(path: string, body?: unknown) => Promise<T>;
  put: <T>(path: string, body?: unknown) => Promise<T>;
  patch: <T>(path: string, body?: unknown) => Promise<T>;
  delete: <T>(path: string) => Promise<T>;
  /** Async — always returns a fresh token (refreshes if needed). Pass true to force refresh. */
  getAuthHeaders: (forceRefresh?: boolean) => Promise<HeadersInit>;
  getBaseUrl: () => string;
  isConfigured: () => boolean;
}

export function useCrmApiClient(): CrmApiClient {
  const { instance } = useMsal();
  const tokenManager = useTokenManager();

  const getAuthHeaders = useCallback(async (forceRefresh = false): Promise<HeadersInit> => {
    const token = await tokenManager.getToken(forceRefresh);
    const { userId, email, roles } = tokenManager.getUserInfo();

    return {
      "Content-Type": "application/json",
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      "X-User-Id": userId,
      "X-User-Email": email,
      "X-User-Roles": roles.join(","),
    };
  }, [tokenManager]);

  const fetchWithAuth = useCallback(
    async <T>(path: string, options: RequestInit = {}): Promise<T> => {
      const headers = await getAuthHeaders();
      const crmBase = getConfig().crmServiceUrl || "";
      const url = `${crmBase}${path}`;
      const response = await fetch(url, {
        ...options,
        headers: { ...headers, ...options.headers } as HeadersInit,
      });

      if (response.status === 401 && !useDevBypass) {
        const retryHeaders = await getAuthHeaders(true);
        const retryResponse = await fetch(url, {
          ...options,
          headers: { ...retryHeaders, ...options.headers } as HeadersInit,
        });

        if (retryResponse.status === 401) {
          try {
            await instance.acquireTokenRedirect({ scopes: ["openid", "profile", "email"] });
          } catch {}
          throw new Error("Session expired. Redirecting to login...");
        }

        if (!retryResponse.ok) {
          const text = await retryResponse.text().catch(() => retryResponse.statusText);
          throw new Error(`HTTP ${retryResponse.status}: ${text}`);
        }

        return retryResponse.json();
      }

      if (!response.ok) {
        const text = await response.text().catch(() => response.statusText);
        throw new Error(`HTTP ${response.status}: ${text}`);
      }

      return response.json();
    },
    [getAuthHeaders, instance],
  );

  return {
    get: <T>(path: string) => fetchWithAuth<T>(path),
    post: <T>(path: string, body?: unknown) =>
      fetchWithAuth<T>(path, { method: "POST", body: body ? JSON.stringify(body) : undefined }),
    put: <T>(path: string, body?: unknown) =>
      fetchWithAuth<T>(path, { method: "PUT", body: body ? JSON.stringify(body) : undefined }),
    patch: <T>(path: string, body?: unknown) =>
      fetchWithAuth<T>(path, { method: "PATCH", body: body ? JSON.stringify(body) : undefined }),
    delete: <T>(path: string) => fetchWithAuth<T>(path, { method: "DELETE" }),
    getAuthHeaders: (force?: boolean) => getAuthHeaders(force),
    getBaseUrl: () => getConfig().crmServiceUrl || "",
    isConfigured: () => !!(getConfig().crmServiceUrl),
  };
}

export default useCrmApiClient;
